/**
 * Settings > Storage (W2-13 settings-keymap; DESIGN.md §4.20, §5.13; ARCHITECTURE.md §10).
 *
 * What this browser keeps for Phiedit and how safe it is:
 *  - usage against the browser's quota, whether storage is persistent ("Keep my data" asks the browser), whether
 *    media live in the private file system (OPFS) or in IndexedDB, and the save state (pending changes);
 *  - the version retention rules and the per-project version budget (min(200 MB, 10 % of the quota));
 *  - the space each project uses, largest first (the largest few at first, every project on request);
 *  - the trash (kept 30 days): restore, delete forever, empty;
 *  - "Download all as backup": every project as .pez + phiedit.json in one zip, streamed by the io worker.
 * Live: health updates come from `PersistClient.onHealth`, the project list follows other tabs.
 */

import { useCallback, useEffect, useState } from 'react';
import type { AppServices } from '../../app/registry/types';
import type { ProjectSummary, StorageHealth } from '../../persist/types';
import { Button, Icon, ProgressBar } from '../../ui';
import { dateStamp, downloadBlob, formatBytes } from './files';
import type { SettingsText } from './hooks';
import { m } from './settings.messages';
import s from './Settings.module.css';

const DAY_MS = 24 * 60 * 60 * 1000;
/** Trash retention (persist: projects in the trash are deleted after 30 days). */
export const TRASH_DAYS = 30;
/** Projects listed before "Show all" (the largest ones). */
export const PROJECTS_SHOWN = 8;
/** Per-project version budget cap (ARCHITECTURE.md §10, persist VersionBudget). */
export const VERSION_BUDGET_CAP = 200 * 1024 * 1024;

/** Version budget of one project for a quota: min(200 MB, 10 % of the quota). */
export function versionBudget(quotaBytes: number): number {
  if (!Number.isFinite(quotaBytes) || quotaBytes <= 0) return VERSION_BUDGET_CAP;
  return Math.min(VERSION_BUDGET_CAP, Math.floor(quotaBytes * 0.1));
}

/** Whole days left before a trashed project is deleted (0 = today). */
export function trashDaysLeft(trashedAt: number, now: number): number {
  return Math.max(0, Math.ceil((trashedAt + TRASH_DAYS * DAY_MS - now) / DAY_MS));
}

interface ProjectsState {
  readonly projects: readonly ProjectSummary[];
  /** When the list was read (for "days left"). */
  readonly at: number;
}

export interface StorageSectionProps {
  readonly services: AppServices;
  readonly text: SettingsText;
}

export function StorageSection(props: StorageSectionProps) {
  const { services, text } = props;
  const { t, locale } = text;
  const persist = services.persist;
  const [health, setHealth] = useState<StorageHealth | null>(null);
  const [healthError, setHealthError] = useState(false);
  const [data, setData] = useState<ProjectsState | null>(null);
  const [listError, setListError] = useState(false);
  const [backup, setBackup] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [allProjects, setAllProjects] = useState(false);

  const loadProjects = useCallback(async (): Promise<void> => {
    try {
      const projects = await persist.listProjects(true);
      setData({ projects, at: Date.now() });
      setListError(false);
    } catch {
      setListError(true);
    }
  }, [persist]);

  useEffect(() => {
    let alive = true;
    persist
      .health()
      .then((h) => {
        if (alive) setHealth(h);
      })
      .catch(() => {
        if (alive) setHealthError(true);
      });
    const offHealth = persist.onHealth((h) => {
      setHealth(h);
      setHealthError(false);
    });
    persist.listProjects(true).then(
      (projects) => {
        if (!alive) return;
        setData({ projects, at: Date.now() });
        setListError(false);
      },
      () => {
        if (alive) setListError(true);
      },
    );
    const offExternal = persist.onExternalChange((e) => {
      if (e.kind === 'projects') void loadProjects();
    });
    const offProgress = services.io.on('progress', (p) => {
      if (p.task === 'backup') setBackup(p.p);
    });
    return () => {
      alive = false;
      offHealth();
      offExternal();
      offProgress();
    };
  }, [persist, services.io, loadProjects]);

  const projects = data?.projects ?? [];
  const live = projects.filter((p) => p.trashedAt === null);
  const trash = projects.filter((p) => p.trashedAt !== null);
  const used = live.reduce((sum, p) => sum + p.bytes, 0);
  const bySize = [...live].sort((a, b) => b.bytes - a.bytes || a.name.localeCompare(b.name, locale));
  const collapsible = bySize.length > PROJECTS_SHOWN;
  const listed = collapsible && !allProjects ? bySize.slice(0, PROJECTS_SHOWN) : bySize;
  const bytes = (n: number): string => formatBytes(n, text);
  const date = (ms: number): string => new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(ms);

  const requestPersistence = async (): Promise<void> => {
    const granted = await persist.requestPersistence().catch(() => false);
    services.ui.toast(
      { key: granted ? m.persistGranted : m.persistDenied },
      { kind: granted ? 'success' : 'warning', durationMs: 8000 },
    );
    try {
      setHealth(await persist.health());
    } catch {
      // The next health event updates the view.
    }
  };

  const restore = async (p: ProjectSummary): Promise<void> => {
    setBusy(true);
    try {
      await persist.restoreProject(p.id);
      services.ui.toast({ key: m.trashRestored, params: { name: p.name } }, { kind: 'success' });
    } catch {
      services.ui.toast({ key: m.storageActionFailed }, { kind: 'error' });
    } finally {
      setBusy(false);
      void loadProjects();
    }
  };

  const deleteForever = async (list: readonly ProjectSummary[]): Promise<void> => {
    if (list.length === 0) return;
    const first = list[0];
    const ok = await services.ui.confirm({
      title:
        list.length === 1 && first !== undefined
          ? { key: m.trashDeleteOneTitle, params: { name: first.name } }
          : { key: m.trashEmptyTitle, params: { count: list.length } },
      body: { key: m.trashDeleteBody },
      confirm: { key: list.length === 1 ? m.trashDeleteConfirm : m.trashEmptyConfirm },
      danger: true,
    });
    if (!ok) return;
    setBusy(true);
    try {
      for (const p of list) await persist.deleteForever(p.id);
      services.ui.toast({ key: m.trashDeleted, params: { count: list.length } }, { kind: 'success' });
    } catch {
      services.ui.toast({ key: m.storageActionFailed }, { kind: 'error' });
    } finally {
      setBusy(false);
      void loadProjects();
    }
  };

  const downloadBackup = async (): Promise<void> => {
    setBackup(0);
    try {
      await persist.flush();
      const blob = await services.io.call('backupAll', { projectIds: live.map((p) => p.id) });
      downloadBlob(blob, `phiedit-backup-${dateStamp()}.zip`);
      services.ui.toast({ key: m.backupDone, params: { count: live.length } }, { kind: 'success' });
    } catch (err) {
      services.ui.toast(
        { key: m.backupFailed, params: { detail: err instanceof Error ? err.message : String(err) } },
        { kind: 'error' },
      );
    } finally {
      setBackup(null);
    }
  };

  const quota = health?.quotaBytes ?? 0;
  const usage = health?.usageBytes ?? 0;

  return (
    <div className={s.storage}>
      <p className={s.blockDesc}>{t(m.storageDesc)}</p>

      {health === null ? (
        <p className={s.blockDesc} role="status">
          {t(healthError ? m.storageUnavailable : m.storageLoading)}
        </p>
      ) : (
        <>
          <ProgressBar
            value={quota > 0 ? usage / quota : null}
            label={{ key: m.storageUsage, params: { used: bytes(usage), quota: bytes(quota) } }}
          />
          <ul className={s.facts}>
            <li data-level={health.persisted ? 'ok' : 'warning'}>
              <Icon name={health.persisted ? 'ShieldCheck' : 'TriangleAlert'} size={16} />
              <span>{t(health.persisted ? m.persistOn : m.persistOff)}</span>
              {!health.persisted && (
                <Button
                  size="sm"
                  icon="Lock"
                  onClick={() => {
                    void requestPersistence();
                  }}
                >
                  {t(m.persistRequest)}
                </Button>
              )}
            </li>
            <li data-level={health.opfs ? 'ok' : 'info'}>
              <Icon name={health.opfs ? 'Database' : 'Info'} size={16} />
              <span>{t(health.opfs ? m.opfsOn : m.opfsOff)}</span>
            </li>
            <li data-level={health.lastWriteError === null && health.workerAlive ? 'ok' : 'error'}>
              <Icon
                name={health.lastWriteError === null && health.workerAlive ? 'CircleCheck' : 'TriangleAlert'}
                size={16}
              />
              <span>
                {health.lastWriteError !== null
                  ? t(m.saveError, { error: health.lastWriteError })
                  : !health.workerAlive
                    ? t(m.saveRestarting)
                    : health.pendingTxns > 0
                      ? t(m.savePending, { count: health.pendingTxns })
                      : t(m.saveOk)}
              </span>
            </li>
          </ul>
        </>
      )}

      <h4 className={s.subTitle}>{t(m.versionsTitle)}</h4>
      <p className={s.blockDesc}>{t(m.versionsRetention)}</p>
      <p className={s.blockDesc}>{t(m.versionsBudget, { budget: bytes(versionBudget(quota)) })}</p>

      <h4 className={s.subTitle}>{t(m.projectsTitle, { count: live.length })}</h4>
      {listError ? (
        <p className={s.blockDesc}>{t(m.storageUnavailable)}</p>
      ) : live.length === 0 ? (
        <p className={s.blockDesc}>{t(m.projectsNone)}</p>
      ) : (
        <>
          <p className={s.blockDesc}>{t(m.projectsTotal, { size: bytes(used) })}</p>
          <table className={s.simpleTable}>
            <thead>
              <tr>
                <th scope="col">{t(m.projectsColName)}</th>
                <th scope="col">{t(m.projectsColSize)}</th>
                <th scope="col">{t(m.projectsColUpdated)}</th>
              </tr>
            </thead>
            <tbody>
              {listed.map((p) => (
                <tr key={p.id}>
                  <td>{p.name === '' ? t(m.projectUntitled) : p.name}</td>
                  <td>{bytes(p.bytes)}</td>
                  <td>{date(p.updatedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {collapsible && (
            <Button
              size="sm"
              variant="ghost"
              icon={allProjects ? 'ChevronUp' : 'ChevronDown'}
              aria-expanded={allProjects}
              onClick={() => {
                setAllProjects((v) => !v);
              }}
            >
              {allProjects
                ? t(m.projectsShowLargest, { count: PROJECTS_SHOWN })
                : t(m.projectsShowAll, { count: bySize.length })}
            </Button>
          )}
        </>
      )}

      <h4 className={s.subTitle}>{t(m.trashTitle, { count: trash.length })}</h4>
      <p className={s.blockDesc}>{t(m.trashDesc, { days: TRASH_DAYS })}</p>
      {trash.length > 0 && (
        <>
          <ul className={s.trashList}>
            {trash.map((p) => (
              <li key={p.id} className={s.trashItem}>
                <span className={s.trashText}>
                  <span className={s.skinName}>{p.name === '' ? t(m.projectUntitled) : p.name}</span>
                  <span className={s.skinMeta}>
                    {t(m.trashMeta, {
                      date: date(p.trashedAt ?? 0),
                      days: trashDaysLeft(p.trashedAt ?? 0, data?.at ?? p.trashedAt ?? 0),
                      size: bytes(p.bytes),
                    })}
                  </span>
                </span>
                <Button
                  size="sm"
                  icon="ArchiveRestore"
                  disabledReason={busy ? { key: m.storageBusy } : null}
                  onClick={() => {
                    void restore(p);
                  }}
                >
                  {t(m.trashRestore)}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  icon="Trash"
                  disabledReason={busy ? { key: m.storageBusy } : null}
                  onClick={() => {
                    void deleteForever([p]);
                  }}
                >
                  {t(m.trashDelete)}
                </Button>
              </li>
            ))}
          </ul>
          <Button
            variant="danger"
            icon="Trash"
            disabledReason={busy ? { key: m.storageBusy } : null}
            onClick={() => {
              void deleteForever(trash);
            }}
          >
            {t(m.trashEmpty)}
          </Button>
        </>
      )}

      <h4 className={s.subTitle}>{t(m.backupTitle)}</h4>
      <p className={s.blockDesc}>{t(m.backupDesc)}</p>
      <div className={s.inline}>
        <Button
          icon="HardDriveDownload"
          disabledReason={
            backup !== null ? { key: m.backupRunning } : live.length === 0 ? { key: m.backupNothing } : null
          }
          onClick={() => {
            void downloadBackup();
          }}
        >
          {t(m.backupDownload)}
        </Button>
        {backup !== null && <ProgressBar value={backup} label={{ key: m.backupProgress }} />}
      </div>
    </div>
  );
}
